import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { inr, numIN, prettyDay, todayLocal } from "@/lib/format";

const EMPTY = { date: todayLocal(), people_reached: "", session0_done: "", clients_joined: "", money_received: "", reflection: "" };
const MIN_ENTRY_DATE = "2026-09-27";

export default function DailyTracker({ entries, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("new"); // new | edit
  const [form, setForm] = useState(EMPTY);

  const openNew = () => {
    setMode("new");
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (e) => {
    setMode("edit");
    setForm({
      date: e.date,
      people_reached: e.people_reached ?? "",
      session0_done: e.session0_done ?? "",
      clients_joined: e.clients_joined ?? "",
      money_received: e.money_received ?? "",
      reflection: e.reflection ?? "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (form.date < MIN_ENTRY_DATE) {
      toast.error("You cannot log activity dated 26 Sep 2026 or earlier");
      return;
    }
    setBusy(true);
    try {
      await api.post("/entries", {
        date: form.date,
        people_reached: Number(form.people_reached) || 0,
        session0_done: Number(form.session0_done) || 0,
        clients_joined: Number(form.clients_joined) || 0,
        money_received: Number(form.money_received) || 0,
        reflection: form.reflection,
      });
      toast.success(mode === "edit" ? "Entry updated" : "Entry saved");
      setOpen(false);
      setForm(EMPTY);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Entry deleted");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sorted = [...(entries || [])].sort((a, b) => (a.date < b.date ? 1 : -1));

  const field = (label, key, type = "number", ph = "0") => (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5 font-mono-num" type={type} data-testid={`daily-${key}-input`}
             value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph} />
    </div>
  );

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center justify-between p-5 sm:p-6 border-b border-border">
        <div>
          <h3 className="font-display text-xl font-bold">Daily Tracker</h3>
          <p className="text-sm text-muted-foreground">Log your outreach, Session 0s, clients and money — every day.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-daily-entry-btn" onClick={openNew}>
            <Plus className="h-4 w-4 mr-2" /> Log day
          </Button>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">{mode === "edit" ? "Edit day" : "Log today's activity"}</DialogTitle>
              <DialogDescription>Record your outreach, Session 0s, clients and money for the day.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Date</Label>
                <Input type="date" className="mt-1.5 font-mono-num" data-testid="daily-date-input" disabled={mode === "edit"} min={MIN_ENTRY_DATE}
                       value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              {field("People I reached out to", "people_reached")}
              {field("Session 0s I did", "session0_done")}
              {field("Clients who joined", "clients_joined")}
              {field("Money received (₹)", "money_received")}
              {Number(form.clients_joined) > 0 && Number(form.money_received) > 0 && (
                <div className="col-span-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm" data-testid="avg-per-client-hint">
                  Average per client: <span className="font-mono-num font-bold text-emerald-800">{inr(Math.round(Number(form.money_received) / Number(form.clients_joined)))}</span>
                  <span className="text-muted-foreground ml-1">({inr(Number(form.money_received))} ÷ {numIN(Number(form.clients_joined))} clients)</span>
                </div>
              )}
              <div className="col-span-2">
                <Label>How did my calls go today? (one line)</Label>
                <Input className="mt-1.5" data-testid="daily-reflection-input" value={form.reflection}
                       onChange={(e) => setForm({ ...form, reflection: e.target.value })} placeholder="Felt confident, 2 warm leads..." />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="daily-tracker-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> {mode === "edit" ? "Update entry" : "Save entry"}</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {sorted.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">No entries yet. Log your first day to start your streak.</div>
      ) : (
        <>
          {/* Mobile: card per day */}
          <div className="md:hidden divide-y divide-border">
            {sorted.map((e) => (
              <div key={e.id} data-testid={`daily-card-${e.date}`} className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{prettyDay(e.date)}</div>
                    <div className="text-xs text-muted-foreground">Day {e.day ?? "—"}{e.week ? ` · Week ${e.week}` : ""} · <span className="text-emerald-700 font-semibold font-mono-num">{numIN(e.points)} pts</span></div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-entry-${e.date}`}>
                      <Pencil className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(e.id)} data-testid={`delete-entry-${e.date}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-muted/60 py-2">
                    <div className="font-mono-num font-bold">{numIN(e.people_reached)}</div>
                    <div className="text-[11px] text-muted-foreground">Reached</div>
                  </div>
                  <div className="rounded-lg bg-muted/60 py-2">
                    <div className="font-mono-num font-bold">{numIN(e.session0_done)}</div>
                    <div className="text-[11px] text-muted-foreground">Session 0s</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 py-2">
                    <div className="font-mono-num font-bold text-emerald-700">{numIN(e.clients_joined)}</div>
                    <div className="text-[11px] text-emerald-700">Clients</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="text-muted-foreground">Received <span className="font-mono-num font-medium text-foreground">{inr(e.money_received)}</span></div>
                  <div className="font-mono-num font-semibold text-amber-700">So far {inr(e.money_so_far)}</div>
                </div>
                {e.reflection ? <p className="text-xs text-muted-foreground italic">"{e.reflection}"</p> : null}
              </div>
            ))}
          </div>

          {/* Desktop/tablet: full table */}
          <div className="overflow-x-auto hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="hidden md:table-cell">Day</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Reached</TableHead>
                <TableHead className="text-right">Session 0s</TableHead>
                <TableHead className="text-right">Clients</TableHead>
                <TableHead className="text-right hidden md:table-cell">Received</TableHead>
                <TableHead className="hidden md:table-cell">Reflection</TableHead>
                <TableHead className="text-right">Money so far</TableHead>
                <TableHead className="text-right">Points</TableHead>
                <TableHead className="text-center hidden md:table-cell">Week</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((e) => (
                <TableRow key={e.id} data-testid={`daily-row-${e.date}`}>
                  <TableCell className="font-mono-num hidden md:table-cell">{e.day ?? "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{prettyDay(e.date)}</TableCell>
                  <TableCell className="text-right font-mono-num">{numIN(e.people_reached)}</TableCell>
                  <TableCell className="text-right font-mono-num">{numIN(e.session0_done)}</TableCell>
                  <TableCell className="text-right font-mono-num text-emerald-700 font-semibold">{numIN(e.clients_joined)}</TableCell>
                  <TableCell className="text-right font-mono-num hidden md:table-cell">{inr(e.money_received)}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-muted-foreground hidden md:table-cell">{e.reflection || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-amber-700">{inr(e.money_so_far)}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-emerald-700" data-testid={`daily-points-${e.date}`}>{numIN(e.points)}</TableCell>
                  <TableCell className="text-center hidden md:table-cell">{e.week ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 font-mono-num">W{e.week}</span> : "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`edit-entry-${e.date}`}>
                        <Pencil className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(e.id)} data-testid={`delete-entry-${e.date}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}
    </div>
  );
}
